// src/components/CreateFolderModal.js - 폴더(프로젝트) 생성 모달

import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faQuestionCircle,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { useAppContext } from "../context/AppContext";
import { apiStream } from "../services/apiClient";
import { Button, Modal, Tooltip } from "./ui";
import "./CreateFolderModal.scss";

const DEFAULT_FORM = { name: "", productService: "", tags: [] };

export default function CreateFolderModal({ isOpen, onClose, onCreateSuccess }) {
  const { createProject } = useAppContext();

  const [newProject, setNewProject] = useState(DEFAULT_FORM);
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [isGeneratingTags, setIsGeneratingTags] = useState(false);
  // AbortController를 state로 두면(=setState) generateTagsWithLLM가 재생성되어 무한 요청 루프가 날 수 있음.
  // ref로 관리하여 함수/이펙트의 안정성을 유지한다.
  const tagGenerationAbortControllerRef = useRef(null);
  const tagGenerationRequestSeqRef = useRef(0);

  const resetForm = useCallback(() => {
    setNewProject(DEFAULT_FORM);
    setSuggestedTags([]);
    setIsGeneratingTags(false);
    try {
      tagGenerationAbortControllerRef.current?.abort();
    } catch (_) {
      // ignore
    }
    tagGenerationAbortControllerRef.current = null;
  }, []);

  const safeClose = useCallback(() => {
    try {
      tagGenerationAbortControllerRef.current?.abort();
    } catch (_) {
      // ignore
    }
    resetForm();
    onClose?.();
  }, [onClose, resetForm]);

  // 모달이 닫히면(또는 언마운트) 정리
  useEffect(() => {
    if (isOpen) return undefined;
    resetForm();
    return undefined;
  }, [isOpen, resetForm]);

  useEffect(() => {
    return () => {
      try {
        tagGenerationAbortControllerRef.current?.abort();
      } catch (_) {
        // ignore
      }
      tagGenerationAbortControllerRef.current = null;
    };
  }, []);

  const generateTagsWithLLM = useCallback(
    async (projectTitle, productUrl = "") => {
      if (!projectTitle || projectTitle.trim().length < 2) {
        setSuggestedTags([]);
        return;
      }

      // "마지막 요청"만 상태를 갱신하도록 request id를 발급
      tagGenerationRequestSeqRef.current += 1;
      const requestId = tagGenerationRequestSeqRef.current;
      const isActiveRequest = () => tagGenerationRequestSeqRef.current === requestId;

      // 이전 요청이 있으면 취소
      if (tagGenerationAbortControllerRef.current) {
        try {
          tagGenerationAbortControllerRef.current.abort();
        } catch (_) {
          // ignore
        }
      }

      const abortController = new AbortController();
      tagGenerationAbortControllerRef.current = abortController;
      setIsGeneratingTags(true);
      setSuggestedTags([]); // 기존 태그 초기화
      // 서버가 스트림을 닫지 않는 케이스 대비: 로딩 문구만 타임아웃으로 종료
      const loadingTimeout = setTimeout(() => {
        if (isActiveRequest()) {
          setIsGeneratingTags(false);
        }
      }, 8000);

      try {
        const response = await apiStream("/workspace/generate-tags", {
          method: "POST",
          body: JSON.stringify({
            project_title: projectTitle,
            product_url: productUrl,
          }),
          signal: abortController.signal,
        });

        if (!response.ok) throw new Error("태그 생성 실패");

        // Server-Sent Events (SSE) 스트리밍 수신 - 청크 경계 안전 처리
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            // 남은 버퍼 처리
            if (buffer.trim()) {
              const frame = buffer.trim();
              if (frame.startsWith("data: ")) {
                const jsonStr = frame.slice(6).trim();
                try {
                  const data = JSON.parse(jsonStr);
                  if (data.tags) {
                    if (isActiveRequest()) {
                      setSuggestedTags(data.tags);
                      setIsGeneratingTags(false);
                    }
                  }
                } catch (e) {
                  console.warn("JSON 파싱 실패");
                }
              }
            }
            break;
          }

          buffer += decoder.decode(value, { stream: true });

          let frameEnd;
          while ((frameEnd = buffer.indexOf("\n\n")) !== -1) {
            const frame = buffer.slice(0, frameEnd).trim();
            buffer = buffer.slice(frameEnd + 2);

            if (frame.startsWith("data: ")) {
              const jsonStr = frame.slice(6).trim();
              try {
                const data = JSON.parse(jsonStr);
                if (data.tags) {
                  if (isActiveRequest()) {
                    setSuggestedTags(data.tags);
                    setIsGeneratingTags(false);
                  }
                }
              } catch (e) {
                console.warn("JSON 파싱 실패");
              }
            }
          }
        }
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.error("태그 생성 오류:", error);
        }
      } finally {
        clearTimeout(loadingTimeout);
        // 이전 요청의 finally가 "새 요청 상태"를 덮어쓰지 않게 가드
        if (isActiveRequest()) {
          setIsGeneratingTags(false);
          if (tagGenerationAbortControllerRef.current === abortController) {
            tagGenerationAbortControllerRef.current = null;
          }
        }
      }
    },
    []
  );

  // 입력값 변경 시 태그 업데이트 (프로젝트명 + URL 함께)
  useEffect(() => {
    if (!isOpen) return undefined;
    const timer = setTimeout(() => {
      if ((newProject.name || "").trim().length >= 3) {
        generateTagsWithLLM(newProject.name, newProject.productService);
      } else {
        // 입력이 짧아지면: 진행 중 요청 중단 + UI를 즉시 "직접 입력" 상태로 복귀
        try {
          tagGenerationAbortControllerRef.current?.abort();
        } catch (_) {
          // ignore
        }
        tagGenerationAbortControllerRef.current = null;
        setIsGeneratingTags(false);
        setSuggestedTags([]);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [isOpen, newProject.name, newProject.productService, generateTagsWithLLM]);

  const handleInputChange = (field, value) => {
    setNewProject((prev) => ({ ...prev, [field]: value }));
  };

  const addTag = (tag) => {
    if (!tag) return;
    if (!newProject.tags.includes(tag)) {
      setNewProject((prev) => ({
        ...prev,
        tags: [...prev.tags, tag],
      }));
    }
  };

  const removeTag = (tagToRemove) => {
    setNewProject((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleCreateProject = async () => {
    // 1. 필수 입력 검증
    if (!newProject.name) {
      alert("프로젝트 제목을 입력해주세요.");
      return;
    }

    if (newProject.tags.length === 0) {
      alert("도메인/태그를 최소 1개 이상 선택하거나 직접 입력해주세요.");
      return;
    }

    // 2. Supabase에 저장 (Context의 createProject = API 호출)
    const createdProject = await createProject({
      name: newProject.name,
      tags: newProject.tags, // 백엔드에서 keywords로 변환
      productUrl: newProject.productService,
    });

    // 3. 상태 초기화 + 닫기
    resetForm();
    onCreateSuccess?.(createdProject);
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      title="새 폴더 생성"
      size="xl"
      className="create-folder-modal"
      bodyClassName="create-folder-modal__body"
      footerClassName="create-folder-modal__footer"
      onClose={safeClose}
      canCloseOutside={false}
      footer={(
        <>
          <Button variant="secondary" onClick={safeClose}>
            취소
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateProject}
            disabled={!newProject.name || newProject.tags.length === 0}
          >
            폴더 생성
          </Button>
        </>
      )}
    >
      <div className="form-layout">
        <div className="form-group">
          <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            폴더 명 <span style={{ color: "#ef4444" }}>*</span>
            <Tooltip
              content={
                <>
                  비즈니스나 서비스를 나타내는 이름입니다.
                  <br />
                  추후 폴더 구분에 사용됩니다.
                </>
              }
              position="right"
            >
              <FontAwesomeIcon
                icon={faQuestionCircle}
                style={{
                  fontSize: "14px",
                  color: "#d1d5db",
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          </label>
          <input
            type="text"
            value={newProject.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            placeholder="예: ABC증권 MTS운영, DEF커머스 웹사이트개선"
            autoFocus
          />
        </div>

        <div className="form-group">
          <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            도메인/관련태그 <span style={{ color: "#ef4444" }}>*</span>
            <Tooltip
              content={
                <>
                  AI가 상황에 적합한 사례를 찾고 <br /> 조사를 준비하는데 도움이
                  됩니다.
                </>
              }
              position="right"
            >
              <FontAwesomeIcon
                icon={faQuestionCircle}
                style={{
                  fontSize: "14px",
                  color: "#d1d5db",
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          </label>
          <p className="form-description">
            {isGeneratingTags ? (
              <span style={{ color: "#3A66FF" }}>
                <FontAwesomeIcon icon={faSpinner} spin /> AI가 관련 태그를 생성
                중입니다...
              </span>
            ) : (
              "최소 1개 이상 선택하거나 직접 입력해주세요 (엔터로 추가)"
            )}
          </p>
          <div className="tags-input-container">
            <div className="selected-tags">
              {newProject.tags.map((tag, index) => (
                <span key={index} className="selected-tag">
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="tag-remove"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <input
              type="text"
              placeholder="엔터를 눌러 태그 추가"
              onKeyPress={(e) => {
                if (e.key === "Enter" && e.target.value.trim()) {
                  addTag(e.target.value.trim());
                  e.target.value = "";
                }
              }}
            />
            {suggestedTags.length > 0 && (
              <div className="suggested-tags">
                <div className="suggested-tags-list">
                  {suggestedTags.map((tag, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`suggested-tag ${
                        newProject.tags.includes(tag) ? "selected" : ""
                      }`}
                      onClick={() => addTag(tag)}
                      disabled={newProject.tags.includes(tag)}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            관련 서비스/제품 이름 혹은 URL주소{" "}
            <span style={{ color: "#9ca3af" }}>(선택)</span>
            <Tooltip
              content={
                <>
                  제품이나 서비스의 이름, 웹사이트 URL, 앱스토어 링크를 입력하세요.
                  <br />
                  AI가 제품 및 서비스에 적합한 조사 방법을 제안하는데 도움이
                  됩니다.
                </>
              }
              position="right"
            >
              <FontAwesomeIcon
                icon={faQuestionCircle}
                style={{
                  fontSize: "14px",
                  color: "#d1d5db",
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          </label>
          <input
            type="text"
            value={newProject.productService}
            onChange={(e) => handleInputChange("productService", e.target.value)}
            placeholder="예: https://example.com, 앱스토어 URL, 서비스명"
          />
        </div>
      </div>
    </Modal>
  );
}
